import { useState, useEffect, useMemo } from 'react';
import { Switch } from './components/ui/switch';
import { Label } from './components/ui/label';
import { Button } from './components/ui/button';
import { Loader2, Star, List, Grid3x3, ArrowUpDown, Filter } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuTrigger,
  DropdownMenuCheckboxItem,
} from './components/ui/dropdown-menu';
import { STATUS_LABELS, STATUSES_ORDER, DISTRICTS } from './types';
import VillasView from './components/VillasView';
import VillaDrawer from './components/VillaDrawer';
import ArchiveDrawer from './components/ArchiveDrawer';
import { Villa, fetchVillas, fetchArchived, updateVilla } from './api';

type ViewMode = 'grid' | 'list';
type SortOption = 'date-desc' | 'date-asc' | 'status-asc' | 'status-desc';

function App() {
  // Проверяем, открыто ли в Telegram WebApp (внутри компонента)
  const isTelegramWebApp = typeof window !== 'undefined' && !!(window as any).Telegram?.WebApp;
  const [viewMode, setViewMode] = useState<ViewMode>('grid');
  const [showOnlyPinned, setShowOnlyPinned] = useState(false);
  const [statusFilters, setStatusFilters] = useState<Set<string>>(new Set());
  const [districtFilters, setDistrictFilters] = useState<Set<string>>(new Set());
  const [villas, setVillas] = useState<Villa[]>([]);
  const [archived, setArchived] = useState<Villa[]>([]);
  const [loading, setLoading] = useState(true);
  const [archiveOpen, setArchiveOpen] = useState(false);
  const [selectedVilla, setSelectedVilla] = useState<Villa | null>(null);
  const [sortBy, setSortBy] = useState<SortOption>('date-desc');

  const loadData = async () => {
    try {
      setLoading(true);
      const [villasData, archivedData] = await Promise.all([
        fetchVillas(),
        fetchArchived(),
      ]);
      setVillas(villasData.villas ?? []);
      setArchived(archivedData.villas ?? []);
    } catch (err) {
      console.error('Failed to load data:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const handleVillaChange = (updatedVilla: Villa) => {
    // Просто обновляем локальное состояние, API вызов уже сделан в компоненте
    setVillas(prev => prev.map(v => v.id === updatedVilla.id ? updatedVilla : v));
    // Также обновляем selectedVilla если это та же вилла
    setSelectedVilla(prev => prev?.id === updatedVilla.id ? updatedVilla : prev);
  };

  const handleArchiveRestore = async (id: string) => {
    try {
      const updated = await updateVilla(id, { status: 'new' });
      setArchived(prev => prev.filter(v => v.id !== id));
      setVillas(prev => [...prev, updated]);
    } catch (err) {
      console.error('Failed to restore villa:', err);
    }
  };

  const handleMoveToArchive = async (id: string) => {
    try {
      await updateVilla(id, { status: 'archived' });
      setVillas(prev => prev.filter(v => v.id !== id));
      setSelectedVilla(null);
    } catch (err) {
      console.error('Failed to archive villa:', err);
    }
  };

  const sortedVillas = useMemo(() => {
    const list = [...villas];
    switch (sortBy) {
      case 'date-asc':
        return list.sort((a, b) => new Date(a.addedAt).getTime() - new Date(b.addedAt).getTime());
      case 'date-desc':
        return list.sort((a, b) => new Date(b.addedAt).getTime() - new Date(a.addedAt).getTime());
      case 'status-asc': {
        // Сначала новые
        return list.sort((a, b) => STATUSES_ORDER.indexOf(a.status) - STATUSES_ORDER.indexOf(b.status));
      }
      case 'status-desc': {
        // Сначала отобранные
        return list.sort((a, b) => STATUSES_ORDER.indexOf(b.status) - STATUSES_ORDER.indexOf(a.status));
      }
      default:
        return list;
    }
  }, [villas, sortBy]);

  const filteredVillas = useMemo(() => {
    let result = sortedVillas;
    if (showOnlyPinned) {
      result = result.filter(v => v.pinned);
    }
    if (statusFilters.size > 0) {
      result = result.filter(v => statusFilters.has(v.status));
    }
    if (districtFilters.size > 0) {
      result = result.filter(v => v.district && districtFilters.has(v.district));
    }
    return result;
  }, [sortedVillas, showOnlyPinned, statusFilters, districtFilters]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex justify-center">
      <div className={isTelegramWebApp ? "w-full" : "w-full max-w-6xl"}>
        <header className="bg-background px-4 py-3 space-y-3">
        {/* Row 1: Title + View toggle + Favorites */}
        <div className="flex items-center justify-between">
          <h1 className="text-lg font-semibold">{filteredVillas.length} вилл</h1>
          <div className="flex items-center gap-1">
            {/* Favorites toggle */}
            <div className="flex items-center gap-1 mr-2">
              {showOnlyPinned ? (
                <Star className="h-4 w-4 text-amber-500 fill-amber-500 cursor-pointer" onClick={() => setShowOnlyPinned(false)} />
              ) : (
                <Star className="h-4 w-4 text-muted-foreground cursor-pointer hover:text-amber-500" onClick={() => setShowOnlyPinned(true)} />
              )}
            </div>
            {/* View mode toggle */}
            <div className="flex items-center bg-muted rounded-lg p-0.5">
              <button
                onClick={() => setViewMode('list')}
                className={`p-1.5 rounded ${viewMode === 'list' ? 'bg-background shadow-sm' : ''}`}
                title="Список"
              >
                <List className={`h-4 w-4 ${viewMode === 'list' ? 'text-foreground' : 'text-muted-foreground'}`} />
              </button>
              <button
                onClick={() => setViewMode('grid')}
                className={`p-1.5 rounded ${viewMode === 'grid' ? 'bg-background shadow-sm' : ''}`}
                title="Сетка"
              >
                <Grid3x3 className={`h-4 w-4 ${viewMode === 'grid' ? 'text-foreground' : 'text-muted-foreground'}`} />
              </button>
            </div>
          </div>
        </div>

        {/* Row 2: Sort + Status filters */}
        <div className="flex items-center gap-2">
          {/* Sort dropdown */}
          <DropdownMenu modal={false}>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm" className="gap-1">
                <ArrowUpDown className="h-3.5 w-3.5" />
                <span className="text-xs">Сортировка</span>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start">
              <DropdownMenuLabel>По дате</DropdownMenuLabel>
              <DropdownMenuRadioGroup value={sortBy} onValueChange={(v) => setSortBy(v as SortOption)}>
                <DropdownMenuRadioItem value="date-desc">Новые сверху</DropdownMenuRadioItem>
                <DropdownMenuRadioItem value="date-asc">Старые сверху</DropdownMenuRadioItem>
                <DropdownMenuLabel className="pt-2">По статусу</DropdownMenuLabel>
                <DropdownMenuRadioItem value="status-asc">Сначала новые</DropdownMenuRadioItem>
                <DropdownMenuRadioItem value="status-desc">Сначала отобранные</DropdownMenuRadioItem>
              </DropdownMenuRadioGroup>
            </DropdownMenuContent>
          </DropdownMenu>

          {/* Status filter dropdown */}
          <DropdownMenu modal={false}>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm" className={`gap-1 ${statusFilters.size > 0 ? 'bg-accent' : ''}`}>
                <Filter className="h-3.5 w-3.5" />
                <span className="text-xs">Статус</span>
                {statusFilters.size > 0 && (
                  <span className="ml-0.5 h-4 px-1 rounded-full bg-primary text-primary-foreground text-[10px] flex items-center">
                    {statusFilters.size}
                  </span>
                )}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="w-48">
              <DropdownMenuLabel>Фильтр по статусу</DropdownMenuLabel>
              {STATUSES_ORDER.map((status) => (
                <DropdownMenuCheckboxItem
                  key={status}
                  checked={statusFilters.has(status)}
                  onCheckedChange={(checked) => {
                    setStatusFilters(prev => {
                      const next = new Set(prev);
                      if (checked) {
                        next.add(status);
                      } else {
                        next.delete(status);
                      }
                      return next;
                    });
                  }}
                >
                  {STATUS_LABELS[status]}
                </DropdownMenuCheckboxItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>

          {/* District filter dropdown */}
          <DropdownMenu modal={false}>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm" className={`gap-1 ${districtFilters.size > 0 ? 'bg-accent' : ''}`}>
                <span className="text-xs">Район</span>
                {districtFilters.size > 0 && (
                  <span className="ml-0.5 h-4 px-1 rounded-full bg-primary text-primary-foreground text-[10px] flex items-center">
                    {districtFilters.size}
                  </span>
                )}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="w-48">
              <DropdownMenuLabel>Фильтр по району</DropdownMenuLabel>
              {DISTRICTS.map((d) => (
                <DropdownMenuCheckboxItem
                  key={d}
                  checked={districtFilters.has(d)}
                  onCheckedChange={(checked) => {
                    setDistrictFilters(prev => {
                      const next = new Set(prev);
                      if (checked) {
                        next.add(d);
                      } else {
                        next.delete(d);
                      }
                      return next;
                    });
                  }}
                >
                  {d}
                </DropdownMenuCheckboxItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>

          {/* Clear filters button */}
          {(statusFilters.size > 0 || districtFilters.size > 0) && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                setStatusFilters(new Set());
                setDistrictFilters(new Set());
              }}
              className="text-xs text-muted-foreground hover:text-foreground"
            >
              Сбросить
            </Button>
          )}
        </div>
      </header>

      <VillasView
        villas={filteredVillas}
        viewMode={viewMode}
        onSelect={setSelectedVilla}
        onUpdate={handleVillaChange}
        onMoveToArchive={handleMoveToArchive}
      />

      {/* Archive button - only show when no filters active */}
      {archived.length > 0 && !showOnlyPinned && statusFilters.size === 0 && districtFilters.size === 0 && (
        <div className="p-4">
          <button
            onClick={() => setArchiveOpen(true)}
            className="w-full py-3 px-4 rounded-lg bg-secondary text-secondary-foreground font-medium"
          >
            Архивные ({archived.length})
          </button>
        </div>
      )}

      <VillaDrawer
        villa={selectedVilla}
        open={selectedVilla !== null}
        onOpenChange={(open) => !open && setSelectedVilla(null)}
        onChange={handleVillaChange}
        onMoveToArchive={handleMoveToArchive}
      />

      <ArchiveDrawer
        open={archiveOpen}
        onOpenChange={setArchiveOpen}
        villas={archived}
        onRestore={handleArchiveRestore}
      />
      </div>
    </div>
  );
}

export default App;
